import { FiArchive, FiPlus } from 'react-icons/fi';
import { useParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { toastSuccess } from '../utils/toasts';
import AdminSectionTitle from '../components/AdminSectionTitle';
import FilterTable, { BaseData } from '../components/Table/Table';
import EmptyTable from '../components/Table/EmptyTable';
import {
  useGetCustomerGroupsQuery, useDeleteCustomerGroupMutation, useGetAllUserEventsQuery, RootState,
} from '../store';
import LoadingLottie from '../components/atoms/LoadingLottie';
import { CustomerGroupDTO } from '../models/customer';
import MenuDropdown from '../components/atoms/MenuDropdown';
import CustomModal from '../components/atoms/CustomModal';
import ViewEditCustomerGroup from '../components/ViewEditCustomerGroup';
import Button from '../components/atoms/Button';
import { IconPlay2 } from '../components/icons';

interface TableRow extends BaseData {
  name: string;
}

const AdminCustomersGroups: React.FC = () => {
  const userData = useSelector((state: RootState) => state.auth.user);
  const { business_id: businessId } = useParams<{ business_id: string }>();
  const { data: customerGroups, isLoading } = useGetCustomerGroupsQuery({ businessId: businessId || '' }, { skip: !businessId });
  const { data: events } = useGetAllUserEventsQuery({ userId: userData.id, businessId });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState<'create' | 'edit' | 'delete'>('create');
  const [customerGroup, setCustomerGroup] = useState<CustomerGroupDTO | null>(null);
  const [deleteCustomerGroup, { isLoading: isDeleting, isSuccess: isDeleted }] = useDeleteCustomerGroupMutation();

  useEffect(() => {
    if (isDeleted) {
      setIsModalOpen(false);
      toastSuccess('Grupo eliminado correctamente');
    }
  }, [isDeleted]);

  const headCells = [
    { id: 'name', label: 'Nombre' },
  ] as const;

  const rows: TableRow[] = useMemo(() => (customerGroups || []).map((c: CustomerGroupDTO) => ({
    id: c.id,
    name: c.name,
  })), [customerGroups]);

  const dropDownOptions = [
    {
      label: 'Editar grupo',
      onClick: (row: CustomerGroupDTO) => {
        const customerGroupRow = customerGroups?.find((c) => c.id === row.id);
        if (!customerGroupRow) {
          throw new Error('Customer group not found');
        }
        setModalType('edit');
        setCustomerGroup(customerGroupRow);
        setIsModalOpen(true);
      },
    },
    {
      label: 'Eliminar grupo',
      onClick: (row: CustomerGroupDTO) => {
        setModalType('delete');
        setCustomerGroup(row);
        setIsModalOpen(true);
      },
    },
  ];

  const actions = (row: TableRow) => {
    const customerGroupRow = customerGroups?.find((c) => c.id === row.id);
    if (!customerGroupRow) {
      throw new Error('Customer group not found');
    }
    return (
      <MenuDropdown<CustomerGroupDTO>
        options={dropDownOptions}
        data={customerGroupRow}
      />
    );
  };

  const customButton = <Button onClick={() => {
    setModalType('create');
    setIsModalOpen(true);
  }} variant='secondary'>
    <div className='flex flex-row gap-2 items-center'>
      <FiPlus />
      <p className='whitespace-nowrap hidden md:block'>Crear grupo</p>
    </div>
  </Button>;

  if (isLoading) {
    return <LoadingLottie />;
  }

  return (
    <div className='flex flex-col gap-4 my-6'>
      <AdminSectionTitle
        title="Productoras"
        subtitle='CRM - Grupos'
        icon={<IconPlay2 className='w-12 h-12 rotate-180' />}
      />
      <div className="glass-surface">
        <h1 className="text-2xl font-medium">Grupos</h1>
        {customerGroups && customerGroups.length > 0 ? <FilterTable
          rows={rows || []}
          headCells={headCells}
          tableId="customer-groups-table"
          actions={actions}
          pagination={{ pageSize: 10, enabled: true }}
          customButtons={[customButton]}
        /> : (
          <EmptyTable
            title="No hay grupos"
            info="No hay grupos de clientes creados"
            icon={FiArchive}
            iconColor="text-primary-600"
            button={[
              {
                label: 'Crear grupo',
                onClick: () => {
                  setModalType('create');
                  setIsModalOpen(true);
                },
                icon: FiPlus,
                variant: 'secondary',
              },
            ]}
          />
        )}
      </div>
      <CustomModal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        hideCancelButton
        overflowVisible
      >
        <>
          {(modalType === 'create') && (
            <div className='flex flex-col gap-4 w-[60vw] max-w-lg'>
              <h1>Crear grupo</h1>
              <ViewEditCustomerGroup customerGroup={null} onClose={() => setIsModalOpen(false)} events={events || []} />
            </div>
          )}
          {modalType === 'edit' && (
            <div className='flex flex-col gap-4 w-[60vw] max-w-lg'>
              <h1>Editar grupo</h1>
              <ViewEditCustomerGroup customerGroup={customerGroup} onClose={() => setIsModalOpen(false)} events={events || []} />
            </div>
          )}
          {modalType === 'delete' && (
            <div className='flex flex-col gap-4 w-[60vw] max-w-lg'>
              <div className='flex flex-col gap-2'>
                <h1>Eliminar grupo</h1>
                <p>¿Estás seguro de querer eliminar este grupo?</p>
                <div className='flex gap-2'>
                  <Button variant='secondary' onClick={() => setIsModalOpen(false)}>Cancelar</Button>
                  <Button variant='primary' onClick={() => deleteCustomerGroup({ businessId: businessId || '', customerGroupId: customerGroup?.id || '' })} loading={isDeleting}>Eliminar</Button>
                </div>
              </div>
            </div>
          )}
        </>
      </CustomModal>
    </div>
  );
};

export default AdminCustomersGroups;
